/**
 * Reimbursement Link Modal
 *
 * Opened when a recipient clicks on a reimbursement_request notification.
 * Allows them to:
 *   1. Pick one of their own debit transactions to link
 *   2. Choose a category for the sender's split portion
 *   3. Accept (creates custom split + settlement) or Decline
 */

import { CategorySelect } from '@/shared/components/category-select';
import { ConfirmationDialog } from '@/shared/components/confirmation-dialog';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import type { TransactionWithCategory } from '@/shared/types';
import { cn } from '@/shared/utils';
import { formatDate, formatMoney } from '@/shared/utils/format';
import { Icon, IconKey } from '@/shared/components';
import { useMemo, useRef, useState } from 'react';
import { useAcceptReimbursement, useDeclineReimbursement, useInfiniteTransactions } from '../../hooks/use-transactions';

interface ReimbursementLinkModalProps {
  transactionId: string;
  notificationId: string;
  notificationMetadata?: Record<string, unknown> | null;
  actionLabel?: string | null;
  onClose: () => void;
}

export function ReimbursementLinkModal({ transactionId, notificationId, notificationMetadata, actionLabel, onClose }: ReimbursementLinkModalProps) {
  // Recipient's debit transactions
  const [search, setSearch] = useState('');
  const {
    data: txnPages,
    isLoading: loadingTxns,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteTransactions({
    search: search || undefined,
    page_size: 20
  });

  // State
  const [selectedTxnId, setSelectedTxnId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [forceAccepted, setForceAccepted] = useState(false);

  // Mutations
  const acceptMutation = useAcceptReimbursement();
  const declineMutation = useDeclineReimbursement();

  // Flatten pages
  const transactions = useMemo(() => {
    if (!txnPages) return [];
    return txnPages.pages.flatMap((p) => p.items);
  }, [txnPages]);

  // Ref-based guard prevents double-submit: refs update synchronously on click,
  // unlike React state / isPending which lag until the next re-render.
  const submittingRef = useRef(false);

  const handleAccept = () => {
    if (!selectedTxnId || !categoryId || submittingRef.current) return;
    submittingRef.current = true;
    acceptMutation.mutate(
      {
        transactionId,
        data: { linked_transaction_id: selectedTxnId, category_id: categoryId, notification_id: notificationId }
      },
      {
        onSuccess: onClose,
        onError: (error) => {
          // If already accepted, the idempotent endpoint returns the settlement
          // via onSuccess. This fallback handles the 400 case (no notification_id).
          if (error instanceof Error && error.message.includes('already been accepted')) {
            setForceAccepted(true);
          }
        },
        onSettled: () => { submittingRef.current = false; },
      }
    );
  };

  const handleDecline = () => {
    declineMutation.mutate(transactionId, { onSuccess: onClose });
  };

  // Check if the reimbursement has already been accepted.
  // Primary: backend-set action_label ("View Details").
  // Fallback: forceAccepted (set when the accept endpoint detects a duplicate).
  const isAlreadyAccepted = actionLabel === 'View Details' || forceAccepted;

  // Read-only view for already-accepted reimbursements (all data from notification metadata)
  if (isAlreadyAccepted) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <div className='flex h-8 w-8 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30'>
                <Icon icon={IconKey.Check} size={16} className='text-green-600 dark:text-green-400' />
              </div>
              Reimbursement Linked
            </DialogTitle>
            <DialogDescription>This reimbursement has been accepted and linked.</DialogDescription>
          </DialogHeader>

          <div className='space-y-3 py-2'>
            {typeof notificationMetadata?.amount === 'string' && (
              <div className='flex justify-between text-sm'>
                <span className='text-muted-foreground'>Amount</span>
                <span className='font-medium'>{formatMoney(notificationMetadata.amount as string)}</span>
              </div>
            )}
            {typeof notificationMetadata?.from_user_name === 'string' && (
              <div className='flex justify-between text-sm'>
                <span className='text-muted-foreground'>From</span>
                <span className='font-medium'>{notificationMetadata.from_user_name}</span>
              </div>
            )}
            {typeof notificationMetadata?.method === 'string' && (
              <div className='flex justify-between text-sm'>
                <span className='text-muted-foreground'>Method</span>
                <span className='font-medium capitalize'>{notificationMetadata.method}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant='outline' onClick={onClose}>Dismiss</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const isAcceptDisabled = !selectedTxnId || !categoryId || acceptMutation.isPending;

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent size='lg' className='max-h-[95vh] h-fit flex flex-col'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <Icon icon={IconKey.ArrowRightLeft} size={20} className='text-accent' />
              Link Reimbursement
            </DialogTitle>
            {notificationMetadata ? (
              <DialogDescription>
                {typeof notificationMetadata.amount === 'string' && (
                  <><span className='font-medium text-text'>{formatMoney(notificationMetadata.amount as string)}</span>{' '}</>
                )}
                {typeof notificationMetadata.from_user_name === 'string' && (
                  <>{'from '}<span className='font-medium text-text'>{notificationMetadata.from_user_name}</span>{' '}</>
                )}
                {typeof notificationMetadata.description === 'string' && (
                  <>{'for '}<span className='font-medium text-text'>{notificationMetadata.description}</span>{' '}</>
                )}
                {typeof notificationMetadata.date === 'string' && (
                  <>{'on '}{formatDate(notificationMetadata.date as string)}</>
                )}
              </DialogDescription>
            ) : null}
          </DialogHeader>

          {/* Step 1: Transaction picker */}
          <div className='flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-3 min-h-0 px-1'>
            <Label className='text-sm font-medium'>Select your transaction to link</Label>

            {/* Search */}
            <div className='relative'>
              <Icon icon={IconKey.Search} size={16} className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground' />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder='Search transactions...' className='pl-9' />
            </div>

            {/* Transaction list */}
            <div className='flex-1 overflow-auto border rounded-lg min-h-[200px] max-h-[300px]'>
              {loadingTxns ? (
                <div className='flex items-center justify-center h-32'>
                  <Icon icon={IconKey.Loading} size={20} className='animate-spin text-muted-foreground' />
                </div>
              ) : transactions.length === 0 ? (
                <div className='flex items-center justify-center h-32 text-sm text-muted-foreground'>No transactions found</div>
              ) : (
                <div className='divide-y divide-border'>
                  {transactions.map((txn) => (
                    <TransactionRow
                      key={txn.id}
                      transaction={txn}
                      selected={txn.id === selectedTxnId}
                      onSelect={() => setSelectedTxnId(txn.id)}
                    />
                  ))}
                  {hasNextPage && (
                    <button
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className='w-full py-2 text-xs text-accent hover:text-accent/80 transition-colors'
                    >
                      {isFetchingNextPage ? <Icon icon={IconKey.Loading} size={12} className='animate-spin mx-auto' /> : 'Load more'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Step 2: Category selector (shown after picking a transaction) */}
            {selectedTxnId && (
              <div className='space-y-2'>
                <Label className='text-sm font-medium'>Category for sender's portion</Label>
                <CategorySelect
                  value={categoryId}
                  onValueChange={setCategoryId}
                  placeholder='Select category...'
                />
              </div>
            )}
          </div>

          <DialogFooter className='flex gap-2 pt-2'>
            <Button
              variant='outline'
              onClick={() => setShowDeclineConfirm(true)}
              disabled={declineMutation.isPending}
              className='text-destructive border-destructive/30 hover:bg-destructive/10'
            >
              {declineMutation.isPending ? <Icon icon={IconKey.Loading} size={16} className='animate-spin mr-2' /> : <Icon icon={IconKey.X} size={16} className='mr-2' />}
              Decline
            </Button>
            <Button onClick={handleAccept} disabled={isAcceptDisabled}>
              {acceptMutation.isPending ? <Icon icon={IconKey.Loading} size={16} className='animate-spin mr-2' /> : <Icon icon={IconKey.Check} size={16} className='mr-2' />}
              Accept & Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        isOpen={showDeclineConfirm}
        title='Decline Reimbursement?'
        description="This will revert the sender's transaction back to a normal expense. They will need to re-mark it if this was a mistake."
        confirmLabel='Decline'
        onConfirm={handleDecline}
        onCancel={() => setShowDeclineConfirm(false)}
      />
    </>
  );
}

/** Single row in the transaction picker list */
function TransactionRow({
  transaction,
  selected,
  onSelect
}: {
  transaction: TransactionWithCategory;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        'w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors',
        selected ? 'bg-accent/10 border-l-2 border-l-accent' : 'hover:bg-secondary/30 border-l-2 border-l-transparent'
      )}
    >
      <div className='flex-1 min-w-0'>
        <div className='text-sm font-medium truncate'>{transaction.description}</div>
        <div className='flex items-center gap-2 text-xs text-muted-foreground'>
          <span>{formatDate(transaction.date)}</span>
          {transaction.category_name && (
            <>
              <span className='text-border'>|</span>
              <span className='flex items-center gap-1'>
                {transaction.category_color && (
                  <span className='h-2 w-2 rounded-full' style={{ backgroundColor: transaction.category_color }} />
                )}
                {transaction.category_name}
              </span>
            </>
          )}
        </div>
      </div>
      <div className='text-sm font-semibold shrink-0'>{formatMoney(transaction.amount)}</div>
      {selected && <Icon icon={IconKey.Check} size={16} className='text-accent shrink-0' />}
    </button>
  );
}
